import { CircularProgress } from '@mui/joy';

import { useEffect, useRef } from 'react';

import ArrowRightIcon from '@/icons/ArrowRightIcon.svg?react';
import { cn } from '@/utils/tailwind';

export function LoadMoreButton(props: {
  onClick: () => void;
  isLoading: boolean;
  isInitialLoad?: boolean;
  minimized?: boolean;
  isVirtual?: boolean;
  loadMoreText?: string;
  className?: string;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const isInitClick = useRef(props.isInitialLoad ?? false);

  const hasIntersected = useRef(false);

  function handleClick() {
    isInitClick.current = true;

    if (!props.isLoading) {
      props.onClick();
    }
  }

  const clickRef = useRef(handleClick);
  clickRef.current = handleClick;

  useEffect(() => {
    if (ref.current) {
      let lastVal = false;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !lastVal && isInitClick.current) {
            if (!props.isVirtual || hasIntersected.current) {
              // if virtual, ignore the first time an intersection is detected
              // because it's triggered by the initial load.
              clickRef.current();
            }
            hasIntersected.current = true;
          }

          lastVal = entry.isIntersecting;
        },
        { root: null, threshold: 0.1 }
      );

      observer.observe(ref.current);
    }
  }, []);

  return (
    <button
      type="button"
      ref={ref}
      className={cn(
        'mt-2 flex items-center justify-center gap-2 rounded-lg p-2 px-4 text-center text-ls-black transition-all',
        'hover:bg-secondary-hover active:bg-secondary disabled:bg-transparent disabled:text-tertiary',
        props.className
      )}
      onClick={handleClick}
      disabled={props.isLoading}
    >
      {props.isLoading ? (
        <>
          <CircularProgress size="sm" color="neutral" sx={{ opacity: 0.5 }} />
          {!props.minimized && <span>Loading...</span>}
        </>
      ) : (
        <>
          {!props.minimized && <span>{props.loadMoreText ?? 'Load more'}</span>}
          <ArrowRightIcon className="rotate-90" />
        </>
      )}
    </button>
  );
}
